<template>
  <div>
    <b-table :current-page="currentPage"
             :fields="fields"
             :items="localProposals"
             :per-page="perPage"
             head-variant="light"
             hover
             responsive
             striped
             @row-clicked="setShowFinalSubmit">
      <template v-slot:top-row="{fields}">
        <proposal-filter :id="id"
                         v-model="localProposals"
                         :fields="fields"
                         :not-include-key="['actions', 'ordinalNumber']"
                         :selected-call="selectedCall"/>
      </template>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage, currentPage}"/>
      </template>

      <template v-slot:cell(actions)="row">
        <div @click="setShowFinalSubmit(row.item, undefined, $event)"
             @keyup.down="setShowFinalSubmit(row.item, undefined, $event)">
          <action-download-pdf :proposal="row.item"/>

          <template v-if="isProposalEditable(row.item)">
            <base-icon-button-comment v-b-modal.modalCericProposal
                                      :disabled="!isUserCericEditor"
                                      title="Edit proposal"
                                      @click="setFocusedId(getId(row.item))"/>

            <base-double-click-submit-button :btn-options="submitBtnOptions"
                                             :disabled="!isUserCericEditor"
                                             :is-confirmation="isFinalSubmit(row.item)"
                                             @click="submitCericProposal(row)"/>

            <base-icon-button-delete v-b-modal.modalAcceptance
                                     :disabled="!isUserCericEditor"
                                     class="red-dark"
                                     title="Delete proposal"
                                     @click="setFocusedRow(row)"/>
          </template>

          <template v-if="hasTeamMembers(row.item)">
            <base-action-expand-details :b-table-row="row"
                                        :title="detailsTitle"/>
          </template>

        </div>
      </template>

      <template v-slot:row-details="row">
        <team-form-no-account v-if="hasTeamMembers(row.item)"
                              v-model="row.item.document_specification.subtype.ceric.team_members"/>
      </template>
    </b-table>

    <b-table-footer :current-page="currentPage"
                    :data-length="localProposals.length"
                    :per-page="perPage"
                    @change-page="changePage">
      <template v-slot>
        <base-per-page-input v-model="perPage"/>
      </template>
    </b-table-footer>

    <modal-acceptance modal-title="Are you sure you want to delete the proposal?"
                      ok-button-text="Delete proposal"
                      @ok="removeCericProposal(focusedRow)"/>
  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import BaseActionExpandDetails from '@/components/actions/BaseActionExpandDetails';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseDoubleClickSubmitButton from '@/components/base-components/BaseDoubleClickSubmitButton';
  import BaseIconButtonComment from '@/components/base-components/base-icons/BaseIconButtonComment';
  import BaseIconButtonDelete from '@/components/base-components/base-icons/BaseIconButtonDelete';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import BTableFooter from '@/components/BTableFooter';
  import ModalAcceptance from '@/components/ModalAcceptance';
  import ProposalFilter from '@/components/ProposalFilter';
  import TeamFormNoAccount from '@/components/TeamFormNoAccount';
  import submitBtnOptions from '@/json/doubleClickSubmitButton';
  import { experiment } from '@/mixins/experiments';

  export default {
    name: 'CericManageTable',
    components: {
      ActionDownloadPdf,
      BaseActionExpandDetails,
      BaseColumnOrdinalNumber,
      BaseDoubleClickSubmitButton,
      BaseIconButtonComment,
      BaseIconButtonDelete,
      BasePerPageInput,
      BTableFooter,
      ModalAcceptance,
      TeamFormNoAccount,
      ProposalFilter,
    },
    mixins: [experiment],
    props: {
      proposals: {
        type: Array,
        default: () => [],
      },
      isUserCericEditor: {
        type: Boolean,
        default: false,
      },
      fields: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        selectedCall: '',
        id: 0,
        currentPage: 1,
        perPage: 20,
        focusedRow: {},
        finalSubmit: '',
        submitBtnOptions,
        detailsTitle: {
          hidden: 'Show team',
          shown: 'Hide team',
        },
        localProposals: [],
      };
    },
    methods: {
      setShowFinalSubmit(proposal, unusedIndex, event) {
        this.finalSubmit = '';
        if (event.target.id === this.submitBtnOptions.attempt.icon.id) {
          this.finalSubmit = this.getId(proposal);
        }
      },
      isFinalSubmit(proposal) {
        return this.getId(proposal) === this.finalSubmit;
      },
      hasTeamMembers(proposal) {
        return proposal.document_specification.subtype.ceric.team_members;
      },
      setFocusedId(objectId) {
        this.$emit('update-focused-id', objectId);
      },
      setFocusedRow(row) {
        this.focusedRow = row;
      },
      async removeCericProposal(row) {
        const documentId = this.getId(row.item);
        const { msg, error } = (await this.axios.delete(`/documents/ceric/${documentId}`)).data;
        if (msg) {
          this.overwriteCericProposal(documentId);
          this.$notify({ type: 'success', title: msg });
        } else {
          this.$notify({ type: 'error', title: error });
        }
      },
      overwriteCericProposal(documentId) {
        this.axios.get(`/documents/ceric/${documentId}`)
          .then((response) => {
            if (response.data.document) {
              this.proposal = response.data.document;
              this.updateProposal();
            } else {
              this.$notify({ type: 'error', title: response.data.error });
            }
          });
      },
      submitCericProposal(row) {
        const documentId = this.getId(row.item);
        this.axios.patch(`/documents/ceric/${documentId}`)
          .then((response) => {
            if (response.data.msg) {
              this.generateAndAddPdf(documentId);
              this.overwriteCericProposal(documentId);
              this.handleSetExperimentNotification(response.data.msg);
            } else {
              this.$notify({ type: 'error', title: response.data.error });
            }
          })
          .catch((error) => {
            if (error.response.data.error) {
              this.generateAndAddPdf(documentId);
              this.overwriteCericProposal(documentId);
              this.$notify({ type: 'error', title: error.response.data.error });
            } else {
              this.$notify({ type: 'error', title: 'Communication error' });
            }
          });
      },
      generateAndAddPdf(documentId) {
        this.axios.put(`/add-rendered-pdf/${documentId}`)
          .then((response) => {
            if (response.data.msg) {
              this.$notify({ type: 'success', title: response.data.msg });
            } else {
              this.$notify({ type: 'error', title: response.data.error });
            }
          })
          .catch(() => {
            this.$notify({
              type: 'error',
              title: 'PDF cannot be generated, contact SOLARIS User Office, please',
            });
          });
      },
      isProposalEditable(proposal) {
        const finalState = proposal.states.name;
        const partialState = proposal.document_specification.subtype.ceric.states.name;
        return [finalState, partialState].every((state) => state === 'draft');
      },
      updateProposal() {
        this.localProposals.forEach((el, idx) => {
          if (this.getId(el) === this.getId(this.proposal)) {
            this.$set(this.localProposals, idx, this.proposal);
          }
        });
      },
      changePage(val) {
        this.currentPage = val;
      },
    },
    watch: {
      proposals: {
        handler(newValue) {
          this.localProposals = newValue ? [...newValue] : [];
          this.$emit('setup-proposal', this.localProposals);
        },
        immediate: true,
      },
    },
  };
</script>

<style lang="scss" scoped>

</style>
